import { useState } from "react";
import axios from "axios";
import { SwapIcon } from "../../../assets/icons/flowbite-icons";
import { Message, ResponseType } from "../../../hooks/use-chat";
import {
  signEvmTransactionWithRetry,
  waitForEVMTransaction,
} from "../../../utils/wallets";
import { TransactionStatusMessage } from "./transaction-status";

enum Transaction_Order_Type {
  APPROVE_PLUGIN = "approve_plugin",
  APPROVE_ALLOWANCE = "approve_allowance",
  SUBMIT_TXN = "submit_txn",
  SIGN_MESSAGE = "sign_message", // there's a flow in Perps v2 that requires user to sign a message and pass it back to the server
}

interface Transaction_Data {
  order: number;
  step: string;
  data: any;
}

interface DisplayDataProps {
  amountIn: string | undefined;
  amountInSymbol: string;
  amountInPrice: string | undefined;
  amountInUSD: string;
  amountOut: string | undefined;
  amountOutSymbol: string;
  amountOutPrice: string | undefined;
  amountOutUSD: string;
  assetTokenSymbol: string | undefined;
  assetTokenPrice: string | undefined;
  remainingAmount: string | undefined;
  percent: string | undefined;
  leverage: string | undefined;
  isLong: boolean | undefined;
  limitPrice: string | undefined;
  stopLossPrice: string | undefined;
  takeProfitPrice: string | undefined;
  orderIndex: string | undefined;
}

interface SignTransactionStruct {
  type: string;
  title: string;
  transactionId: string;
  buttonMessage: string;
  displayData: DisplayDataProps | null;
  transactions: Transaction_Data[] | null;
}

export const SignTransactionView = ({
  message,
  setMessages,
  accessToken,
  setError,
}: {
  message: string;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  accessToken: string | null;
  setError: React.Dispatch<React.SetStateAction<string>>;
}) => {
  const data: SignTransactionStruct = JSON.parse(message);

  const [loading, setLoading] = useState<boolean>(false);
  const [transactionSignature, setTransactionSignature] = useState<string>("");
  const [transactionChain, setTransactionChain] = useState<string>("");

  async function submitTransactionStatus(status: string, signature: string) {
    try {
      if (!data.transactionId) return;
      const response = await axios.post(
        `${process.env.REACT_APP_CREATOR_ENDPOINT}/submit/transaction-status`,
        {
          status,
          signature,
          transactionId: data.transactionId,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.status !== 200) {
        console.error("Failed to submit transaction status");
      }
    } catch (e: any) {
      console.error("error in submitTransactionStatus: \n", e);
      setError(
        "There seems to be an error submitting the transaction. Please check your wallet and the Kinetix Finance Dashboard for any possible status updates, since your transaction may have possibly been successful but an error may have been received here. If you don't see any updates in your wallet or the Kinetix Dashboard, then please try again."
      );
    }
  }

  async function signTransaction() {
    try {
      if (!data.transactions) return;
      const transactions = data.transactions?.sort((a, b) => a.order - b.order);

      let lastTransactionHash = "";
      let lastTransactionChain = "";
      for (let i = 0; i < transactions.length; i++) {
        setLoading(true);
        const transaction = transactions[i];
        switch (transaction.step) {
          case Transaction_Order_Type.SIGN_MESSAGE:
            // sign message
            break;
          default:
            const transactionHash = await signEvmTransactionWithRetry(
              transaction.data,
              3
            );
            // wait for the transaction to be confirmed
            await waitForEVMTransaction(
              transactionHash?.toString(),
              transaction.data.chainId
            );
            lastTransactionHash = transactionHash.toString();
            lastTransactionChain = transaction.data.chainId.toString();
            break;
        }
      }

      // set transaction signature
      setTransactionSignature(lastTransactionHash);
      setTransactionChain(lastTransactionChain);
      const transactionStatusMessage: TransactionStatusMessage = {
        status: "success",
        content: lastTransactionHash,
        chain: transactionChain,
      };
      submitTransactionStatus("success", lastTransactionHash);
      setMessages((m: Message[]) => [
        ...m,
        {
          agent: "ui",
          message: "",
          component: ResponseType.TRANSACTION_STATUS,
          data: JSON.stringify(transactionStatusMessage),
        },
      ]);

    } catch (e: any) {
      console.error("error in signTransaction SignTransactionView", e);
      const transactionStatusMessage: TransactionStatusMessage = {
        status: "error",
        content:
          e.response?.data?.error ?? e.response?.data?.message ?? e.message,
        chain: transactionChain,
      };
      submitTransactionStatus("failure", "");
      setMessages((m: Message[]) => [
        ...m,
        {
          agent: "ui",
          message: "",
          component: ResponseType.TRANSACTION_STATUS,
          data: JSON.stringify(transactionStatusMessage),
        },
      ]);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <div
        className={`bg-gray-800 border  shadow-sm rounded-lg p-4 mt-4 flex flex-col items-center gap-4 min-w-full ${transactionSignature ? "border-green-900" : "border-gray-700"
          } ${loading ? "animate-pulse" : ""}`}
      >
        {/* Title */}
        <div className="flex flex-col items-center gap-4">
          <h3 className="text-lg font-semibold">{data.title}</h3>
        </div>

        {/* Display Data */}
        <div className="bg-gray-700 border border-gray-600 rounded-lg p-3 flex justify-between items-center gap-4 w-full h-70px">
          {/* Input Token */}
          <div className="flex flex-row items-start gap-2">
            <div className="flex flex-col justify-center gap-1">
              <span className="text-white font-medium text-sm leading-6">
                {data?.displayData?.amountIn
                  ? Number(data?.displayData?.amountIn).toFixed(4)
                  : ""}{" "}
                {data?.displayData?.amountInSymbol}
              </span>
              <span className="text-gray-4000 text-sm leading-6">
                ${Number(data?.displayData?.amountInUSD).toFixed(2)}
              </span>
            </div>
          </div>

          {data.displayData?.amountOut && ( // PerpsV2 Liquidity doesn't have output token
            <>
              <div>
                <SwapIcon />
              </div>
              {/* Output Token */}
              <div className="flex flex-row items-start gap-2">
                <div className="flex flex-col justify-center gap-1">
                  <span className="text-white font-medium text-sm leading-6">
                    {data?.displayData?.amountOut
                      ? Number(data?.displayData?.amountOut).toFixed(4)
                      : ""}{" "}
                    {data?.displayData?.amountOutSymbol}
                  </span>
                  <span className="text-gray-400 text-sm leading-6">
                    ${Number(data?.displayData?.amountOutUSD).toFixed(2)}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Details */}
        {data?.displayData?.amountInPrice && (
          <div className="flex flex-row items-center px-1 w-full">
            <span className="text-white font-semibold text-sm leading-6">
              {data?.displayData?.amountInSymbol} Price
            </span>
            <span className="text-white text-sm leading-6 ml-auto">
              ${Number(data?.displayData?.amountInPrice).toFixed(2)}
            </span>
          </div>
        )}
        {data?.displayData?.amountOutPrice && (
          <div className="flex flex-row items-center px-1 w-full">
            <span className="text-white font-semibold text-sm leading-6">
              {data?.displayData?.amountOutSymbol} Price
            </span>
            <span className="text-white text-sm leading-6 ml-auto">
              ${Number(data?.displayData?.amountOutPrice).toFixed(2)}
            </span>
          </div>
        )}
        {data?.displayData?.assetTokenSymbol && (
          <div className="flex flex-row items-center px-1 w-full">
            <span className="text-white font-semibold text-sm leading-6">
              Asset: {data?.displayData?.assetTokenSymbol}
            </span>
          </div>
        )}
        {data?.displayData?.leverage && (
          <div className="flex flex-row items-center px-1 w-full">
            <span className="text-white font-semibold text-sm leading-6">
              Leverage: {data?.displayData?.leverage}x
            </span>
          </div>
        )}
        {data?.displayData?.remainingAmount && (
          <div className="flex flex-row items-center px-1 w-full">
            <span className="text-white font-semibold text-sm leading-6">
              Remaining: $
              {Number(data?.displayData?.remainingAmount).toFixed(2)}
            </span>
          </div>
        )}
        {data?.displayData?.percent && (
          <div className="flex flex-row items-center px-1 w-full">
            <span className="text-white font-semibold text-sm leading-6">
              Percent: {data?.displayData?.percent}%
            </span>
          </div>
        )}
        {data?.displayData?.orderIndex && (
          <div className="flex flex-row items-center px-1 w-full">
            <span className="text-white font-semibold text-sm leading-6">
              Order Id: {data?.displayData?.orderIndex}
            </span>
          </div>
        )}
        {data?.displayData?.limitPrice && (
          <div className="flex flex-row items-center px-1 w-full">
            <span className="text-white font-semibold text-sm leading-6">
              Limit Price: ${Number(data?.displayData?.limitPrice).toFixed(2)}
            </span>
          </div>
        )}
        {data?.displayData?.stopLossPrice && (
          <div className="flex flex-row items-center px-1 w-full">
            <span className="text-white font-semibold text-sm leading-6">
              Stop Loss Price: $
              {Number(data?.displayData?.stopLossPrice).toFixed(2)}
            </span>
          </div>
        )}
        {data?.displayData?.takeProfitPrice && (
          <div className="flex flex-row items-center px-1 w-full">
            <span className="text-white font-semibold text-sm leading-6">
              Take Profit Price: $
              {Number(data?.displayData?.takeProfitPrice).toFixed(2)}
            </span>
          </div>
        )}

        {/* Submit Button */}
        {!transactionSignature && (
          <button
            className="bg-gradient-to-r from-[#EF533D] to-[#F39537] text-white font-semibold rounded-lg p-2 w-full"
            onClick={signTransaction}
            disabled={loading}
          >
            {!loading ? data.buttonMessage : "Waiting for signature..."}
          </button>
        )}
      </div>
    </>
  );
};
